import type { Money } from '../api/types'

/**
 * Money arrives from Pydantic as a string. Everything that does arithmetic or
 * feeds a chart goes through here first.
 */
export function num(value: Money | null | undefined): number {
  if (value === null || value === undefined) return 0
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const decimal = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const whole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

/** "J$1,234.56" — the currency staff see everywhere else in the product. */
export function money(value: Money | null | undefined): string {
  return `J$${decimal.format(num(value))}`
}

/** "J$1,235" — for tight table cells and axis labels. */
export function moneyWhole(value: Money | null | undefined): string {
  return `J$${whole.format(num(value))}`
}

/** "J$1.2M" — for chart axes, where width is scarce. */
export function moneyCompact(value: Money | null | undefined): string {
  const amount = num(value)
  const magnitude = Math.abs(amount)
  if (magnitude >= 1_000_000) return `J$${(amount / 1_000_000).toFixed(1)}M`
  if (magnitude >= 1_000) return `J$${Math.round(amount / 1_000)}k`
  return `J$${Math.round(amount)}`
}

export function count(value: number | null | undefined): string {
  return whole.format(value ?? 0)
}

/** "12.5%". Returns an em dash for null, which means "not available". */
export function percent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  return `${value.toFixed(digits)}%`
}

/** Signed, for period-over-period deltas: "+12.5%". */
export function delta(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(digits)}%`
}

/** "9 Sep 2026" from a YYYY-MM-DD or ISO datetime string. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** A filter range for a subtitle; an open end reads as "since" or "until". */
export function rangeLabel(from: string | null, to: string | null): string {
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`
  if (from) return `Since ${formatDate(from)}`
  if (to) return `Until ${formatDate(to)}`
  return 'All time'
}

/** "9 Sep 2026, 3:17 pm" */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "3:17 pm" */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })
}

/** "2h 05m" / "45m" between two instants (the second defaults to now). */
export function duration(from: string, to?: string | null): string {
  const minutes = Math.max(0, Math.round(((to ? new Date(to) : new Date()).getTime() - new Date(from).getTime()) / 60000))
  const hours = Math.floor(minutes / 60)
  return hours ? `${hours}h ${String(minutes % 60).padStart(2, '0')}m` : `${minutes}m`
}

/** Short axis label from YYYY-MM-DD: "9 Sep". */
export function shortDate(value: string): string {
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** "2026-09" -> "Sep 2026", for the customer monthly series. */
export function monthLabel(value: string): string {
  const [year, month] = value.split('-')
  if (!year || !month) return value
  const date = new Date(Number(year), Number(month) - 1, 1)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

/** "1h 05m" or "9m" from a number of minutes. */
export function minutes(value: number | null): string {
  if (value === null) return '—'
  const hours = Math.floor(value / 60)
  return hours ? `${hours}h ${String(Math.round(value % 60)).padStart(2, '0')}m` : `${Math.round(value)}m`
}

/** "14:00" from an hour-of-day number. */
export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

export function fullName(first: string | null, last: string | null): string {
  return [first, last].filter(Boolean).join(' ') || 'Unknown'
}

/** Turns invoice_status / payment_type values into readable labels. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—'
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

/**
 * "Manor Centre Branch" -> "Manor Centre".
 *
 * The word is redundant under a column headed Branch, and the full names are
 * wide enough to wrap a table cell onto three lines without it.
 */
export function branchName(name: string | null | undefined): string {
  if (!name) return '—'
  return name.replace(/\s+Branch$/i, '')
}
